import { ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { SearchService } from '../../services/search.service';
import { SummaryService } from '../../services/summary.service';
import { MapsErrorService } from '../../services/maps-error.service';
import { MapsSessionService } from '../../services/maps-session.service';
import { SavedSearchesService } from '../../services/saved-searches.service';
import { ContactService } from '../../services/contact.service';
import { NotifySuccess } from '../../services/notify';
import type {
    MapsCandidate,
    MapsContactCandidateStatus,
    MapsContactSendResult,
    MapsQualification,
    MapsQualificationConfigEntry,
    MapsSavedSearch,
    MapsSearchBrief,
    MapsSearchTrace,
    MapsTraceCandidate,
} from '../../models/maps.types';
import { ApplyFilters, EmptyFilterState, type MapsFilterState } from '../../utils/filtering';
import { SortCandidates, type MapsSortKey } from '../../utils/sorting';
import {
    DEFAULT_PAGE_SIZE,
    IsPageSizeOption,
    PAGE_SIZE_OPTIONS,
    ReadSessionPageSize,
    WriteSessionPageSize,
    type PageSizeOption,
} from '../../utils/session-prefs';

@Component({
    selector: 'maps-search-page',
    standalone: false,
    templateUrl: './search-page.component.html',
    styleUrl: './search-page.component.scss',
})
export class SearchPageComponent implements OnInit, OnDestroy {
    /** Combined free-text input — JD, posts, longform notes, guidance, all together. */
    inputText = '';
    isSearching = false;
    /** Admin-token flag — drives the admin nav surfaces. */
    isAdmin = false;
    /** Live MAPS.Setting flag from mapsSessionValidate — when true, the
     *  pipeline panel renders for all authenticated users (not only admins).
     *  Default false; toggled by admins in /admin/configuration. */
    showPipelinePanelToAll = false;
    // showCareerStage was retired with the career-stage chip in the STN card
    // rebuild (client mockup, 2026-08); the maps.ui.showCareerStage setting and
    // session flag remain server-side but no card surface reads them.
    /** Live default result count (= per-export cap, post 6-8 meeting). Drives
     *  the Select All cap and the export-button cap so a single admin knob
     *  in /admin/configuration controls both. 30 is the conservative fallback
     *  used until mapsSessionValidate resolves. */
    defaultResultCount = 30;
    /** Live per-send recipient cap for the contact modal (maps.contact.sendCap
     *  via mapsSessionValidate). 25 mirrors the server default until resolved. */
    contactSendCap = 25;
    errorMessage: string | null = null;
    rawResults: MapsCandidate[] = [];
    /** True once a search has returned — gates the "N candidates" meta line. */
    private hasResultMeta = false;
    searchId: string | null = null;
    /** SearchLog id of the PRE-weighting result set, captured on the first
     *  configured rerun and cleared on restore/reset. "Restore original
     *  rankings" replays this snapshot (exact original ordering, zero LLM
     *  cost) instead of re-running the pipeline — a full rerun is not
     *  order-stable (8-11 deployed-run D8 finding). */
    private originalSearchId: string | null = null;
    isGeneratingSummaries = false;
    /** True once summary generation has exhausted its retries — cards swap
     *  skeletons for a "Summary unavailable — Retry" affordance. */
    summariesFailed = false;

    /** Server-echoed APPLIED qualification configuration — feeds the panel. */
    qualifications: MapsQualification[] = [];
    /** True while an Apply-changes rerun is in flight (distinct from a fresh search). */
    isApplyingWeights = false;
    /** True when the current results came from a configured (Apply-changes)
     *  rerun — drives the panel's "Restore original rankings" button state. */
    configApplied = false;
    /** The request that produced the current results, snapshotted at Submit so an
     *  Apply rerun uses the SUBMITTED inputs, not a since-edited textarea. */
    private lastRequest: { jobDescription: string; uploadIds?: string[] } | null = null;

    /** How the intake agent decomposed the request (read-only "how it read this"). */
    brief: MapsSearchBrief | null = null;
    /** Per-stage actual I/O for the technical drill-down. */
    trace: MapsSearchTrace | null = null;
    briefOpen = false;
    /** Open nested I/O drill-downs, keyed (e.g. 'brief.in', 'rerank.out'). */
    openSections = new Set<string>();
    /** Panel section element — scroll target for the bottom close control. */
    @ViewChild('briefPanel') private briefPanel?: ElementRef<HTMLElement>;

    selectedCandidateIds = new Set<string>();

    filters: MapsFilterState = EmptyFilterState();
    sortKey: MapsSortKey = 'bestMatch';

    /** Client-side pagination over the filtered/sorted results. The page-size
     *  choice persists for the browser session (sessionStorage). */
    readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
    pageSize: PageSizeOption = ReadSessionPageSize() ?? DEFAULT_PAGE_SIZE;
    currentPage = 1;

    /** Placeholder cards shown while a fresh search is in flight. */
    readonly skeletonSlots = [0, 1, 2, 3] as const;
    extractedUploadIds: string[] = [];
    isUploadBusy = false;
    /** Total files attached toward the cap (includes uploading/failed, not just ready). */
    uploadFileCount = 0;
    readonly maxFiles = 4;

    // Staged progress messaging, FRD §3.3. The cosmetic rotation covers the
    // first seconds until the async poll delivers real pipeline stages (a cold
    // search runs minutes, not the 5–30s this copy originally assumed).
    private static readonly PROGRESS_MESSAGES = [
        'Searching candidates…',
        'Building your result set…',
        'Reviewing results…',
    ];
    /** Whitelisted server log() stage keys → user-facing progress copy. Keys
     *  not listed here (per-stage diagnostics, counters) are ignored. */
    private static readonly STAGE_LABELS: Record<string, string> = {
        'received': 'Reading your job description…',
        'extracting search brief': 'Analyzing requirements…',
        'brief extracted': 'Scanning the member universe…',
        'retrieval returned': 'Reviewing candidate profiles…',
        'reranked': 'Ranking candidates…',
        'inserting SearchLog': 'Finalizing results…',
    };
    searchProgressMessage = SearchPageComponent.PROGRESS_MESSAGES[0];
    private progressTimer: ReturnType<typeof setInterval> | null = null;

    constructor(
        private readonly searchService: SearchService,
        private readonly summaryService: SummaryService,
        private readonly errors: MapsErrorService,
        private readonly zone: NgZone,
        private readonly cdr: ChangeDetectorRef,
        private readonly session: MapsSessionService,
        private readonly savedSearches: SavedSearchesService,
        private readonly contactService: ContactService,
        private readonly hostElement: ElementRef<HTMLElement>,
    ) {}

    async ngOnInit(): Promise<void> {
        // The session is already valid here (mapsAccessGuard ran). We grab the
        // isAdmin flag (admin nav surfaces) and the live showPipelinePanelToAll
        // setting (lets admins expose the pipeline panel to all partner users).
        await this.RefreshSessionFlags();

        // Chips read the shared saved-search page (one query feeds the header
        // badge, the drawer, and these chips).
        this.pageSubscription = this.savedSearches.Page$.subscribe((page) => {
            this.zone.run(() => {
                if (page) this.recentSearches = page.items;
                this.cdr.detectChanges();
            });
        });
        void this.LoadRecentSearches();

        // Saved-searches drawer restores: live subscription while this page is
        // mounted; the pending pickup covers a drawer click made from another
        // route (the drawer navigates here, then this init runs). The handler
        // consumes the pending id so a later remount can't replay it.
        this.restoreSubscription = this.savedSearches.RestoreRequests$.subscribe((searchId) => {
            // Zombie guard: an embedding host can leak a detached instance of
            // this page whose subscription outlives its DOM (MJ Explorer's
            // dashboard-resource wrapper races concurrent loads and drops the
            // loser's ComponentRef without destroy). A disconnected instance
            // must not swallow the restore — leave the pending id for the
            // mounted instance's init pickup.
            if (!this.hostElement.nativeElement.isConnected) return;
            this.savedSearches.ConsumePendingRestore();
            void this.RestoreSavedSearch(searchId);
        });
        const pending = this.savedSearches.ConsumePendingRestore();
        if (pending) void this.RestoreSavedSearch(pending);

        // Contact sends (from the modal): optimistically stamp the succeeded
        // recipients as Sent, then re-hydrate from the server rollups. Same
        // zombie guard as restores — a detached instance must not react.
        this.sendsSubscription = this.contactService.Sends$.subscribe((result) => {
            if (!this.hostElement.nativeElement.isConnected) return;
            this.zone.run(() => {
                const now = new Date().toISOString();
                for (const r of result.recipients) {
                    if (r.status !== 'Sent') continue;
                    const existing = this.contactStatuses.get(r.candidateId);
                    this.contactStatuses.set(r.candidateId, {
                        candidateId: r.candidateId,
                        lastStatus: 'Sent',
                        lastContactedAt: now,
                        contactCount: (existing?.contactCount ?? 0) + 1,
                    });
                }
                this.cdr.detectChanges();
            });
            if (this.searchId) void this.HydrateContactStatuses(this.searchId);
        });
    }

    private restoreSubscription: Subscription | null = null;
    private pageSubscription: Subscription | null = null;
    private sendsSubscription: Subscription | null = null;

    // -------------------------------------------------------- saved searches

    /** The most recent saved searches (chips under the search box). */
    recentSearches: MapsSavedSearch[] = [];
    /** True while a saved-search restore is in flight (guards double clicks). */
    isRestoring = false;

    /** Refresh the shared saved-search page — on init and after every
     *  completed search (the title is written server-side at completion).
     *  Failure is silent: chips/badge are accelerators, never worth a banner. */
    private async LoadRecentSearches(): Promise<void> {
        try {
            await this.savedSearches.Refresh();
        } catch {
            // Leave whatever chips we last had.
        }
    }

    /**
     * Reopen a saved search wholesale from its snapshot — candidates,
     * ordering, summaries, applied weighting — with zero LLM cost, and refill
     * the textarea for edit-and-rerun. No progress stages: this is a single
     * fast read, not a pipeline run.
     */
    async RestoreSavedSearch(searchId: string): Promise<void> {
        if (this.isSearching || this.isApplyingWeights || this.isRestoring) return;
        this.isRestoring = true;
        try {
            const restored = await this.savedSearches.Restore(searchId);
            this.zone.run(() => {
                this.ResetSearchState();
                this.inputText = restored.jobDescription;
                this.rawResults = restored.results;
                this.searchId = restored.searchId;
                this.qualifications = restored.qualifications ?? [];
                this.brief = restored.brief ?? null;
                this.trace = null; // per-run observability doesn't persist on the snapshot
                this.hasResultMeta = true;
                // Weighting reruns work from a restored search like a live one.
                this.lastRequest = { jobDescription: restored.jobDescription };
                NotifySuccess('Loaded search');
                this.cdr.detectChanges();
            });
            void this.HydrateContactStatuses(restored.searchId);
            // Rare: a snapshot whose summary generation never completed.
            // The server short-circuits candidates that already carry text,
            // so this only pays for the genuinely missing ones.
            if (restored.results.some((c) => !c.whySuggested) && restored.results.length > 0) {
                this.zone.run(() => {
                    this.isGeneratingSummaries = true;
                    this.cdr.detectChanges();
                });
                void this.generateSummaries(restored.searchId);
            }
        } catch (err) {
            this.zone.run(() => {
                this.errorMessage = this.errors.FromApollo(err).message;
                this.cdr.detectChanges();
            });
        } finally {
            this.zone.run(() => {
                this.isRestoring = false;
                this.cdr.detectChanges();
            });
        }
    }

    /** Pull the live admin flags for this session. Called on init AND at the
     *  start of every search (7-28 finding F2): the settings are cached
     *  server-side with a ~60s TTL and the config page promises "changes take
     *  effect on the next Search" — a one-time ngOnInit read broke that
     *  promise for any page left open across a settings change. */
    private async RefreshSessionFlags(): Promise<void> {
        try {
            const status = await this.session.Validate();
            this.zone.run(() => {
                this.isAdmin = status.valid && status.isAdmin;
                this.showPipelinePanelToAll = status.valid && status.showPipelinePanelToAll;
                if (status.valid && status.defaultResultCount > 0) {
                    this.defaultResultCount = status.defaultResultCount;
                }
                if (status.valid && status.contactSendCap > 0) {
                    this.contactSendCap = status.contactSendCap;
                }
                this.cdr.detectChanges();
            });
        } catch {
            this.isAdmin = false; // fail closed — hide the technical panel
            this.showPipelinePanelToAll = false;
            // defaultResultCount stays at the conservative class default
        }
    }

    /** Combined gate the pipeline panel reads. Admins always see it; partner
     *  users see it only when the admin Configuration tab has flipped the
     *  live setting on. */
    get canSeePipelinePanel(): boolean {
        return this.isAdmin || this.showPipelinePanelToAll;
    }

    // -------------------------------------------------------------- validation

    /**
     * Min text length only applies when no uploads are attached. The server
     * is the source of truth for the 50-char floor on the combined JD, but
     * we mirror it here to avoid a round-trip on obvious cases.
     */
    get isInputValid(): boolean {
        const len = this.inputText.trim().length;
        if (this.extractedUploadIds.length > 0) return len <= 50_000;
        return len >= 50 && len <= 50_000;
    }

    /** Below the 50-char search floor (uploads bypass it) — drives the hint
     *  copy so the user learns WHY Search is disabled (Test Results 7-15-26). */
    get isBelowMinimum(): boolean {
        const len = this.inputText.trim().length;
        return this.extractedUploadIds.length === 0 && len > 0 && len < 50;
    }

    get canSearch(): boolean {
        return this.isInputValid && !this.isSearching && !this.isUploadBusy && !this.isApplyingWeights;
    }

    get showEmptyState(): boolean {
        // Only before the first successful search. Once a search has produced a
        // searchId, the results section owns the view (including the zero-result
        // "no candidates matched" state), so the panel stays reachable.
        return !this.searchId && !this.isSearching && !this.errorMessage;
    }

    // ---------------------------------------------------------- result pipeline

    /** The position's state from the brief; null when the JD names no work
     *  location (disables the same-state filter). */
    get positionState(): string | null {
        return this.brief?.officeLocation?.region || null;
    }

    get results(): MapsCandidate[] {
        // Missing location/tz/state data never excludes (honest blanks) and
        // Best Match preserves the server order — both contracts live in the
        // pure utils (utils/filtering.ts, utils/sorting.ts) with specs.
        return SortCandidates(ApplyFilters(this.rawResults, this.filters), this.sortKey);
    }

    get selectedCount(): number {
        return this.selectedCandidateIds.size;
    }

    /** "N candidates" for the results header, counting the FILTERED list —
     *  the client-side location filters shrink what's shown, and a static
     *  server count reads stale next to a filtered grid (regression run
     *  2026-07-23). */
    get resultMetaMessage(): string | null {
        return this.hasResultMeta ? `${this.results.length} candidates` : null;
    }

    // ------------------------------------------------------------- pagination

    get totalPages(): number {
        return Math.max(1, Math.ceil(this.results.length / this.pageSize));
    }

    /** Current page clamped to the valid range — filters can shrink the set
     *  under a later page, and clamping beats resetting mid-interaction. */
    get safeCurrentPage(): number {
        return Math.min(Math.max(1, this.currentPage), this.totalPages);
    }

    /** The slice of filtered/sorted results the grid actually renders. */
    get pagedResults(): MapsCandidate[] {
        const start = (this.safeCurrentPage - 1) * this.pageSize;
        return this.results.slice(start, start + this.pageSize);
    }

    /** Pager renders whenever paging could matter — visible even on a single
     *  page at a large page size, so the per-page selector stays reachable. */
    get showPagination(): boolean {
        return this.results.length > this.pageSizeOptions[0];
    }

    OnPageChange(page: number): void {
        this.currentPage = page;
        // Defer until the new page's cards have rendered, then reposition
        // instantly. A smooth scroll here is unreliable: the card swap's
        // render work cancels the animation mid-flight (reads as an awkward
        // half-snap), and throttled tabs stall it entirely. An instant jump
        // to the top of the new page is the predictable pagination behavior;
        // the grid's scroll-margin-top keeps it clear of the sticky header.
        setTimeout(() => this.ScrollToGridTop(), 0);
    }

    OnPageSizeChange(size: number): void {
        if (!IsPageSizeOption(size)) return;
        this.pageSize = size;
        WriteSessionPageSize(size);
        this.currentPage = 1;
    }

    private ScrollToGridTop(): void {
        document.querySelector('.results .grid')?.scrollIntoView({ behavior: 'auto', block: 'start' });
    }

    /** Show the "how MAPS read this" panel only when the brief has content. */
    get hasBrief(): boolean {
        const b = this.brief;
        if (!b) return false;
        return (
            b.retrievalQueries.length > 0 ||
            b.researchTopics.length > 0 ||
            b.hardRequirements.length > 0 ||
            b.softPreferences.length > 0 ||
            b.exclusions.length > 0 ||
            b.unverifiable.length > 0
        );
    }

    ToggleBrief(): void {
        this.briefOpen = !this.briefOpen;
    }

    /** Bottom-of-results "Back to top" — scrolls to the search box. On
     *  standalone the window is the scroller (no scrollable ancestor exists,
     *  so this falls through to window.scrollTo — the original behavior); in
     *  an embedded host (MJExplorer surface) the page scrolls inside a
     *  container, so scroll the nearest scrollable ancestor instead.
     *
     *  The container branch assigns `scrollTop` rather than calling
     *  `scrollTo({behavior:'smooth'})`. Smooth scrolling simply does not work on
     *  that container — measured 2026-08-21 in the MJExplorer surface, both
     *  mechanisms are silent no-ops while the plain assignment moves it:
     *
     *    scrollTo({top:0, behavior:'smooth'})        → scrollTop unchanged
     *    scroll-behavior:smooth + scrollTop = 0      → scrollTop unchanged
     *    scroll-behavior:auto   + scrollTop = 0      → scrolls immediately
     *
     *  So the embedded host gets an instant scroll that works rather than an
     *  animated one that does nothing (the button was dead here before this).
     *  The window branch keeps its smooth request, which does work on standalone.
     *  Do not add `scroll-behavior: smooth` to the surface — it would turn this
     *  assignment back into a no-op. */
    BackToTop(): void {
        let el: HTMLElement | null = this.hostElement.nativeElement;
        while (el) {
            const overflowY = getComputedStyle(el).overflowY;
            if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
                el.scrollTop = 0;
                return;
            }
            el = el.parentElement;
        }
        if (typeof window.scrollTo === 'function') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    /** Bottom-of-panel close — collapses the methodology body and brings the
     *  (now-closed) panel header back into view so the user isn't stranded
     *  wherever the removed content left the viewport. */
    CollapseBrief(): void {
        this.briefOpen = false;
        const el = this.briefPanel?.nativeElement;
        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    /** Nested I/O drill-downs inside the panel. */
    IsSectionOpen(key: string): boolean {
        return this.openSections.has(key);
    }

    ToggleSection(key: string): void {
        if (this.openSections.has(key)) this.openSections.delete(key);
        else this.openSections.add(key);
    }

    /** First card, used to show one concrete summary input/output example in the trace. */
    get exampleCandidate(): MapsCandidate | null {
        return this.rawResults[0] ?? null;
    }

    /** Trace candidates carrying structured-fit / org-country flags — the
     *  step-4 "Soft constraint flags" table (ordering-only signals). */
    get FitFlaggedTraceCandidates(): MapsTraceCandidate[] {
        return (this.trace?.candidates ?? []).filter((c) => c.fitFlags.length > 0);
    }

    /** Human-readable chips for step 1's "Eligibility constraints — applied as
     *  location gate" line (7-27 C-4.3: the gate was invisible in the panel;
     *  eligibility items deliberately never appear in Required). */
    get EligibilityConstraintChips(): string[] {
        const lc = this.brief?.locationConstraint;
        if (!lc) return [];
        const chips: string[] = [];
        if (lc.citizenshipCountry) chips.push(`${lc.citizenshipCountry} citizenship required`);
        if (lc.onsite && lc.country) chips.push(`Onsite in ${lc.country}`);
        if (lc.visaSponsorship === false) chips.push('No visa sponsorship');
        return chips;
    }

    /** Friendly label for a brief target stage in step 1's "Target level"
     *  chips (client 7-29: level extraction must be auditable in the panel). */
    StageLabel(stage: string): string {
        switch (stage) {
            case 'Student': return 'Student';
            case 'EarlyCareer': return 'Early career';
            case 'MidCareer': return 'Mid-career';
            case 'Established': return 'Established';
            default: return stage;
        }
    }

    /** Per-stage counts for the "how MAPS works" flow panel (derived from results). */
    get pipelineStats(): {
        total: number;
        hydrated: number;
        withPublication: number;
        tiers: { Strong: number; Medium: number; Weak: number };
    } {
        const tiers = { Strong: 0, Medium: 0, Weak: 0 };
        let hydrated = 0;
        let withPublication = 0;
        for (const c of this.rawResults) {
            if (c.matchTier in tiers) tiers[c.matchTier as keyof typeof tiers]++;
            if (c.source === 'mj') hydrated++;
            if (c.relevantPublication) withPublication++;
        }
        return { total: this.rawResults.length, hydrated, withPublication, tiers };
    }

    /** Applied REQUIRED qualifications (configured rerun) — for the pipeline trace. */
    get requiredQualifications(): MapsQualification[] {
        return this.qualifications.filter((q) => !q.removed && q.classification === 'required');
    }

    /** Applied PREFERRED qualifications grouped by tier (configured rerun) — for the
     *  pipeline trace, so the "behind the scenes" view reflects the weighting the
     *  recruiter applied, not just the brief's guidance. */
    get preferredTierGroups(): { label: string; items: MapsQualification[] }[] {
        const tiers: { key: 'veryImportant' | 'important' | 'niceToHave'; label: string }[] = [
            { key: 'veryImportant', label: 'Very important' },
            { key: 'important', label: 'Important' },
            { key: 'niceToHave', label: 'Nice to have' },
        ];
        return tiers
            .map((t) => ({
                label: t.label,
                items: this.qualifications.filter((q) => !q.removed && q.classification === 'preferred' && q.tier === t.key),
            }))
            .filter((g) => g.items.length > 0);
    }

    get selectedIdsInOrder(): string[] {
        return this.results
            .filter((c) => this.selectedCandidateIds.has(c.candidateId))
            .map((c) => c.candidateId);
    }

    IsCandidateSelected(c: MapsCandidate): boolean {
        return this.selectedCandidateIds.has(c.candidateId);
    }

    TrackByCandidateId(_index: number, c: MapsCandidate): string {
        return c.candidateId;
    }

    OnSelectionChanged(event: { candidateId: string; selected: boolean }): void {
        if (event.selected) {
            this.selectedCandidateIds.add(event.candidateId);
        } else {
            this.selectedCandidateIds.delete(event.candidateId);
        }
    }

    /** Visible Select-All / Export cap. Equals the admin-tunable
     *  defaultResultCount (Configuration tab → Result Set) per the 6-8 client
     *  meeting; the same number bounds the result set, the Select All ceiling,
     *  and the per-export cap. */
    get exportCap(): number {
        return this.defaultResultCount;
    }

    /** Select the first `exportCap` results in the current sort/filter order.
     *  Per the 6-8 client meeting: "we probably want to have this Select All /
     *  None buttons treatment somewhere." */
    SelectAllVisible(): void {
        this.selectedCandidateIds = new Set(
            this.results.slice(0, this.exportCap).map((c) => c.candidateId)
        );
    }

    ClearSelection(): void {
        this.selectedCandidateIds = new Set();
    }

    get hasResults(): boolean {
        return this.results.length > 0;
    }

    /** True when every visible card up to the export cap is already selected. */
    get isAllVisibleSelected(): boolean {
        const head = this.results.slice(0, this.exportCap);
        if (head.length === 0) return false;
        return head.every((c) => this.selectedCandidateIds.has(c.candidateId));
    }

    OnFiltersChange(next: MapsFilterState): void {
        this.filters = next;
        this.currentPage = 1;
    }

    OnSortChange(next: MapsSortKey): void {
        this.sortKey = next;
        this.currentPage = 1;
    }

    OnExtractedUploadIdsChange(ids: string[]): void {
        this.extractedUploadIds = ids;
    }

    OnUploadBusyChange(busy: boolean): void {
        this.isUploadBusy = busy;
    }

    OnFileCountChange(count: number): void {
        this.uploadFileCount = count;
    }

    /** True once the 4-file cap is reached — drives the disabled upload button. */
    get uploadsFull(): boolean {
        return this.uploadFileCount >= this.maxFiles;
    }

    /** Programmatically open the file picker from the hero "Upload a File" button. */
    TriggerUpload(): void {
        if (this.isSearching || this.uploadsFull) return;
        const input = document.getElementById('upload-input') as HTMLInputElement | null;
        input?.click();
    }

    // ----------------------------------------------------------------- submit

    ngOnDestroy(): void {
        this.stopProgress();
        this.restoreSubscription?.unsubscribe();
        this.pageSubscription?.unsubscribe();
        this.sendsSubscription?.unsubscribe();
    }

    private startProgress(): void {
        let ix = 0;
        this.searchProgressMessage = SearchPageComponent.PROGRESS_MESSAGES[0];
        this.progressTimer = setInterval(() => {
            ix = Math.min(ix + 1, SearchPageComponent.PROGRESS_MESSAGES.length - 1);
            this.searchProgressMessage = SearchPageComponent.PROGRESS_MESSAGES[ix];
            this.cdr.detectChanges();
        }, 4000);
    }

    private stopProgress(): void {
        if (this.progressTimer) {
            clearInterval(this.progressTimer);
            this.progressTimer = null;
        }
    }

    /** Real pipeline progress from the async search poll. The first mapped
     *  stage supersedes the cosmetic ticker for the rest of the run. */
    private OnSearchStage(stage: string): void {
        const label = SearchPageComponent.STAGE_LABELS[stage];
        if (!label) return;
        this.stopProgress();
        this.zone.run(() => {
            this.searchProgressMessage = label;
            this.cdr.detectChanges();
        });
    }

    /** Reset all search output state (results, brief, trace, selection). */
    private ResetSearchState(): void {
        this.errorMessage = null;
        this.rawResults = [];
        this.hasResultMeta = false;
        this.searchId = null;
        this.isGeneratingSummaries = false;
        this.summariesFailed = false;
        // The drawer and contact modal hold references into the outgoing set.
        this.experienceCandidate = null;
        this.contactCandidates = null;
        this.contactStatuses = new Map();
        this.brief = null;
        this.trace = null;
        this.openSections.clear();
        this.selectedCandidateIds = new Set();
        this.qualifications = [];
        this.lastRequest = null;
        this.isApplyingWeights = false;
        this.configApplied = false;
        this.originalSearchId = null;
        this.currentPage = 1;
        // A new search can carry a different (or no) office location — a stale
        // checked same-state filter must not survive across searches.
        this.filters = EmptyFilterState();
    }

    /** The qualification panel can rerun only once an initial search has run. */
    get canReapply(): boolean {
        return this.lastRequest !== null;
    }

    // ------------------------------------------------------- experience drawer

    /** Candidate whose full profile the experience drawer is showing; null = closed. */
    experienceCandidate: MapsCandidate | null = null;

    OpenExperienceDrawer(candidate: MapsCandidate): void {
        this.experienceCandidate = candidate;
    }

    CloseExperienceDrawer(): void {
        this.experienceCandidate = null;
    }

    // ---------------------------------------------------------- contact modal

    /** Recipients the contact modal is showing; null = closed. Opens from a
     *  card's Contact, the experience drawer's footer (stacking above it),
     *  and the bulk bar's Contact all. */
    contactCandidates: MapsCandidate[] | null = null;

    /** Per-candidate contact rollups behind the "Contacted" chips. Hydrated by
     *  ONE batched query per result set; patched optimistically on each send. */
    contactStatuses = new Map<string, MapsContactCandidateStatus>();

    ContactStatusFor(candidateId: string): MapsContactCandidateStatus | null {
        return this.contactStatuses.get(candidateId) ?? null;
    }

    OnContactSent(_result: MapsContactSendResult): void {
        // State updates ride the ContactService.Sends$ subscription (which
        // also covers sends from a future non-search surface); the output
        // exists so the template wiring is explicit and testable.
    }

    /**
     * Fire-and-forget chip hydration for the current result set. Stale-run
     * guarded on searchId (the generateSummaries pattern); failure is silent —
     * a chip is an accelerator, never worth a banner.
     */
    private async HydrateContactStatuses(searchId: string): Promise<void> {
        const ids = this.rawResults.map((c) => c.candidateId);
        if (ids.length === 0) return;
        try {
            const statuses = await this.contactService.Statuses(ids);
            if (this.searchId !== searchId) return; // a newer run owns the grid
            this.zone.run(() => {
                this.contactStatuses = new Map(statuses.map((s) => [s.candidateId, s]));
                this.cdr.detectChanges();
            });
        } catch {
            // Leave whatever chips we last had.
        }
    }

    OpenContactModal(candidates: MapsCandidate[]): void {
        if (candidates.length === 0) return;
        this.contactCandidates = candidates;
    }

    /** Bulk "Contact all": the selected candidates in current display order. */
    OpenContactModalForSelection(): void {
        const byId = new Map(this.rawResults.map((c) => [c.candidateId, c]));
        this.OpenContactModal(
            this.selectedIdsInOrder
                .map((id) => byId.get(id))
                .filter((c): c is MapsCandidate => c !== undefined)
        );
    }

    CloseContactModal(): void {
        this.contactCandidates = null;
    }

    /**
     * Apply-changes rerun from the qualification panel. Deliberately does NOT
     * reuse Submit()/ResetSearchState() — clearing rawResults would unmount the
     * results section (and the panel with it), losing collapse state + edits.
     * We keep the current results on screen until the new set arrives, and on
     * failure we leave `qualifications` untouched so the panel's edits survive.
     */
    async OnApplyWeights(config: MapsQualificationConfigEntry[]): Promise<void> {
        await this.RerunSearch(config);
    }

    /** "Restore original rankings" — replay the pre-weighting snapshot (exact
     *  original ordering, near-instant, no new SearchLog row). Falls back to a
     *  full unconfigured rerun only if the replay fails. */
    async OnRestoreWeights(): Promise<void> {
        await this.RestoreOriginalRanking();
    }

    /**
     * Replay the stashed pre-weighting result set via the saved-search restore
     * read. Deliberately narrower than RestoreSavedSearch: the JD context is
     * unchanged, so `inputText` and `lastRequest` are NOT touched — a
     * subsequent Apply must keep working from the same submitted inputs.
     */
    private async RestoreOriginalRanking(): Promise<void> {
        if (this.isSearching || this.isApplyingWeights || this.isRestoring) return;
        if (!this.originalSearchId) {
            // Unreachable while `configApplied` gates the button; cheap insurance.
            await this.RerunSearch(null);
            return;
        }
        this.isRestoring = true;
        let restoreFailed = false;
        try {
            const restored = await this.savedSearches.Restore(this.originalSearchId);
            this.zone.run(() => {
                this.rawResults = restored.results;
                // The experience drawer holds a reference into the replaced set.
                this.experienceCandidate = null;
                this.qualifications = restored.qualifications ?? [];
                this.configApplied = false;
                this.brief = restored.brief ?? null;
                this.trace = null; // per-run observability doesn't persist on the snapshot
                this.searchId = restored.searchId;
                this.originalSearchId = null;
                this.hasResultMeta = true;
                this.selectedCandidateIds = new Set();
                this.currentPage = 1;
                NotifySuccess('Original rankings restored');
                this.cdr.detectChanges();
            });
            void this.HydrateContactStatuses(restored.searchId);
            // Rare: the original snapshot's summary generation never completed.
            if (restored.results.some((c) => !c.whySuggested) && restored.results.length > 0) {
                this.zone.run(() => {
                    this.isGeneratingSummaries = true;
                    this.cdr.detectChanges();
                });
                void this.generateSummaries(restored.searchId);
            }
        } catch (err) {
            console.error('[maps.searchPage] restore-original failed, falling back to rerun', err);
            restoreFailed = true;
        } finally {
            this.zone.run(() => {
                this.isRestoring = false;
                this.cdr.detectChanges();
            });
        }
        // Outside the finally so the isRestoring flag is already clean: the
        // user always gets their restore, worst case at the old rerun cost.
        if (restoreFailed) {
            await this.RerunSearch(null);
        }
    }

    // -------------------------------------------------------- weighting drawer

    /** Weighting drawer open state (STN rebuild — the panel became a drawer
     *  behind the toolbar's "Adjust weighting" button). */
    weightingOpen = false;

    /** Toolbar badge: applied non-off qualifications (required + preferred). */
    get weightingBadgeCount(): number {
        return this.qualifications.filter((q) => !q.removed).length;
    }

    OpenWeightingDrawer(): void {
        this.weightingOpen = true;
    }

    CloseWeightingDrawer(): void {
        this.weightingOpen = false;
    }

    /** Drawer "Update results": close it and run the configured rerun — the
     *  page's progress treatment takes over (same path as the old panel). */
    OnApplyWeightsFromDrawer(config: MapsQualificationConfigEntry[]): void {
        this.weightingOpen = false;
        void this.OnApplyWeights(config);
    }

    OnRestoreWeightsFromDrawer(): void {
        this.weightingOpen = false;
        void this.OnRestoreWeights();
    }

    private async RerunSearch(config: MapsQualificationConfigEntry[] | null): Promise<void> {
        if (!this.lastRequest || this.isApplyingWeights || this.isSearching) return;
        this.isApplyingWeights = true;
        this.errorMessage = null;
        this.startProgress();
        const t0 = performance.now();
        const previousSearchId = this.searchId;
        try {
            const result = await this.searchService.Search(
                config ? { ...this.lastRequest, qualificationConfig: config } : { ...this.lastRequest },
                (stage) => this.OnSearchStage(stage)
            );
            this.stopProgress();
            this.zone.run(() => {
                // Capture-once: the FIRST configured rerun stashes the baseline
                // snapshot id so apply→apply→restore returns the true
                // pre-weighting set. A fresh unconfigured run IS a new baseline.
                if (config !== null) {
                    if (this.originalSearchId === null) this.originalSearchId = previousSearchId;
                } else {
                    this.originalSearchId = null;
                }
                this.rawResults = result.results;
                // The experience drawer holds a reference into the replaced
                // result set — close it rather than showing a stale profile.
                this.experienceCandidate = null;
                // Hydrate the panel from the server's echoed configuration, not
                // from client state.
                this.qualifications = result.qualifications ?? [];
                this.configApplied = config !== null;
                this.brief = result.brief ?? null;
                this.trace = result.trace ?? null;
                this.searchId = result.searchId;
                this.hasResultMeta = true;
                // Candidate ids may have changed under the new filter — drop stale selections.
                this.selectedCandidateIds = new Set();
                this.currentPage = 1;
                this.isApplyingWeights = false;
                // No candidates → nothing to summarize; don't show a stuck spinner.
                this.isGeneratingSummaries = result.results.length > 0;
                this.cdr.detectChanges();
            });
            if (result.results.length > 0) {
                void this.generateSummaries(result.searchId);
            }
            void this.HydrateContactStatuses(result.searchId);
        } catch (err) {
            console.error('[maps.searchPage] apply-weights rerun failed', {
                elapsedMs: Math.round(performance.now() - t0),
                err,
            });
            this.stopProgress();
            this.zone.run(() => {
                this.errorMessage = this.errors.FromApollo(err).message;
                this.isApplyingWeights = false;
                this.cdr.detectChanges();
            });
        }
    }

    /**
     * Clear Search (Test Results 6-9-26 #2): wipe the input box AND all
     * results/output state so the recruiter starts fresh. Disabled mid-search.
     */
    ClearSearch(): void {
        if (this.isSearching || this.isApplyingWeights) return;
        this.inputText = '';
        this.ResetSearchState();
    }

    async Submit(): Promise<void> {
        if (!this.canSearch) return;
        this.isSearching = true;
        this.ResetSearchState();
        this.startProgress();
        // Refresh the live admin flags per search (7-28 finding F2): they were
        // read once in ngOnInit, so a settings change never reached an
        // already-open page — contradicting the config page's "changes take
        // effect on the next Search". Fire-and-forget: the flags only matter
        // when results render, minutes after this resolves.
        void this.RefreshSessionFlags();

        // Snapshot the SUBMITTED inputs so an "Apply changes" rerun reruns these,
        // not a since-edited textarea.
        const request = {
            jobDescription: this.inputText,
            uploadIds: this.extractedUploadIds.length > 0 ? this.extractedUploadIds : undefined,
        };
        this.lastRequest = request;

        const t0 = performance.now();
        try {
            const result = await this.searchService.Search(request, (stage) => this.OnSearchStage(stage));
            const tAfterService = performance.now();
            this.stopProgress();
            this.zone.run(() => {
                this.rawResults = result.results;
                this.brief = result.brief ?? null;
                this.trace = result.trace ?? null;
                this.searchId = result.searchId;
                this.qualifications = result.qualifications ?? [];
                this.hasResultMeta = true;
                this.isSearching = false;
                // Cards render now with skeleton Why-Suggested / Things boxes;
                // summaries fill in a single pass when the mutation returns.
                this.isGeneratingSummaries = true;
                this.cdr.detectChanges();
            });
            // Browser-side instrumentation: MJ logger isn't bundled into the
            // Angular app, so we keep console.* for observability in DevTools.
            console.log('[maps.searchPage] render committed', {
                serviceMs: Math.round(tAfterService - t0),
                totalMs: Math.round(performance.now() - t0),
                resultCount: this.rawResults.length,
            });
            void this.generateSummaries(result.searchId);
            void this.HydrateContactStatuses(result.searchId);
            // The completed search is now a saved search (titled server-side).
            void this.LoadRecentSearches();
        } catch (err) {
            console.error('[maps.searchPage] search failed', {
                elapsedMs: Math.round(performance.now() - t0),
                err,
            });
            this.stopProgress();
            this.zone.run(() => {
                this.errorMessage = this.errors.FromApollo(err).message;
                this.isSearching = false;
                this.cdr.detectChanges();
            });
        }
    }

    /** Card-visible retry for a failed summary pass. Cheap on the server: the
     *  resolver short-circuits candidates whose summaries are already persisted
     *  on the snapshot, so replaying a dropped response returns instantly. */
    RetrySummaries(): void {
        if (!this.searchId || this.isGeneratingSummaries) return;
        this.summariesFailed = false;
        this.isGeneratingSummaries = true;
        this.cdr.detectChanges();
        void this.generateSummaries(this.searchId);
    }

    /**
     * Block-and-return summary generation (TRD §6.4): one round-trip, then
     * patch every card in a single render pass.
     *
     * Retries: the call runs ~90s for a full result set, and a dropped
     * response (observed HTTP 0, stage 2026-07-13) used to strand the cards on
     * skeletons forever even though the server had persisted every summary.
     * Two bounded retries recover that case at near-zero cost (server-side
     * snapshot short-circuit); only after all attempts fail do the cards show
     * the explicit "Summary unavailable — Retry" state.
     */
    private async generateSummaries(searchId: string): Promise<void> {
        const RETRY_DELAYS_MS = [3_000, 10_000];
        for (let attempt = 0; ; attempt++) {
            try {
                const { summaries } = await this.summaryService.Generate(searchId);
                // A newer search may have started while this one was in flight;
                // never patch another run's cards.
                if (this.searchId !== searchId) return;
                const byId = new Map(summaries.map((s) => [s.candidateId, s]));
                this.zone.run(() => {
                    this.rawResults = this.rawResults.map((c) => {
                        const s = byId.get(c.candidateId);
                        return s
                            ? { ...c, whySuggested: s.whySuggested, thingsToLookInto: s.thingsToLookInto }
                            : c;
                    });
                    this.isGeneratingSummaries = false;
                    this.summariesFailed = false;
                    this.cdr.detectChanges();
                });
                return;
            } catch (err) {
                console.error('[maps.searchPage] summary generation failed', {
                    attempt: attempt + 1,
                    willRetry: attempt < RETRY_DELAYS_MS.length,
                    err,
                });
                if (this.searchId !== searchId) return;
                if (attempt >= RETRY_DELAYS_MS.length) {
                    this.zone.run(() => {
                        this.isGeneratingSummaries = false;
                        this.summariesFailed = true;
                        this.cdr.detectChanges();
                    });
                    return;
                }
                await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt]));
                if (this.searchId !== searchId) return;
            }
        }
    }
}
